import { useEffect, useState } from "react";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { formatCount } from "./analytics-utils";

/**
 * Chart colours come from the kit's tokens. Recharts writes them as SVG presentation
 * attributes, which cannot hold `var()`, so the channels are read from the document and
 * re-read when the colour mode or brand changes on <html>.
 */
const TOKENS = {
  primary: "--pe-action",
  // The second series uses the warning hue: it differs from the action blue in lightness as
  // well as hue in every brand, which a brand-coloured second series would not (Odyssey is blue).
  secondary: "--pe-warning",
  grid: "--pe-border",
  axis: "--pe-fg-subtle",
} as const;

export type ChartTone = keyof Pick<typeof TOKENS, "primary" | "secondary">;
type ChartColors = Record<keyof typeof TOKENS, string>;

const readColors = (): ChartColors => {
  const style = getComputedStyle(document.documentElement);
  const read = (name: string) => {
    const channels = style.getPropertyValue(name).trim();
    return channels ? `rgb(${channels})` : "currentColor";
  };
  return {
    primary: read(TOKENS.primary),
    secondary: read(TOKENS.secondary),
    grid: read(TOKENS.grid),
    axis: read(TOKENS.axis),
  };
};

export function useChartColors(): ChartColors {
  const [colors, setColors] = useState(readColors);
  useEffect(() => {
    const observer = new MutationObserver(() => setColors(readColors()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-brand"],
    });
    return () => observer.disconnect();
  }, []);
  return colors;
}

export type ChartSeries = {
  key: string;
  name: string;
  tone: ChartTone;
  kind: "line" | "area" | "bar";
};

type Props = {
  data: Record<string, string | number | null>[];
  series: ChartSeries[];
  height: number;
  formatValue?: (value: number) => string;
};

const ChartTooltip = ({
  active,
  payload,
  label,
  formatValue,
}: TooltipProps<number, string> & { formatValue: (value: number) => string }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-surface-raised px-3 py-2 text-xs text-fg shadow">
      <div className="mb-1 font-medium">{label}</div>
      {payload.map((p) => (
        <div key={String(p.dataKey)} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-sm" style={{ background: p.color }} />
          <span className="text-fg-muted">{p.name}</span>
          <span className="ml-auto pl-3 font-mono tabular-nums">
            {formatValue(Number(p.value))}
          </span>
        </div>
      ))}
    </div>
  );
};

/** A daily series drawn at the card's full width; any mix of lines, areas and bars. */
const TimeSeriesChart = ({ data, series, height, formatValue = formatCount }: Props) => {
  const colors = useChartColors();
  const tick = { fill: colors.axis, fontSize: 12 };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} stroke={colors.grid} />
        <XAxis
          dataKey="label"
          tick={tick}
          tickLine={false}
          axisLine={{ stroke: colors.grid }}
          minTickGap={24}
        />
        <YAxis
          width={52}
          tick={tick}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => formatValue(v)}
        />
        <Tooltip
          content={<ChartTooltip formatValue={formatValue} />}
          cursor={{ stroke: colors.grid, fill: colors.grid, fillOpacity: 0.4 }}
        />
        {series.map((s) => {
          const color = colors[s.tone];
          if (s.kind === "bar") {
            return (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.name}
                fill={color}
                radius={[2, 2, 0, 0]}
                maxBarSize={24}
                isAnimationActive={false}
              />
            );
          }
          if (s.kind === "area") {
            return (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={color}
                strokeWidth={2}
                fill={color}
                fillOpacity={0.12}
                dot={false}
                isAnimationActive={false}
              />
            );
          }
          return (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          );
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default TimeSeriesChart;
